import React from 'react';
import { ShieldCheck, FileCheck2, ScrollText, Lock } from 'lucide-react';

const RetentionAndPrivacy = () => {
  return (
    <section id="retention" className="my-20">
      <div className="bg-gradient-to-br from-apple-800/60 to-apple-900/60 backdrop-blur-md rounded-3xl border border-apple-700/40 p-8 sm:p-12 relative overflow-hidden">
        <div className="absolute -left-32 -top-32 w-72 h-72 rounded-full bg-sortio-primary/10 blur-[100px]" />
        <div className="absolute -right-32 -bottom-32 w-72 h-72 rounded-full bg-sortio-secondary/10 blur-[100px]" />

        <div className="relative z-10">
          <div className="flex items-center mb-6">
            <div className="w-12 h-12 rounded-full bg-sortio-primary/15 border border-sortio-primary/30 flex items-center justify-center mr-4">
              <ShieldCheck className="text-sortio-primary" size={22} />
            </div>
            <span className="text-xs font-medium uppercase tracking-wider text-sortio-primary">
              Privacy, retention, and ethics
            </span>
          </div>

          <h2 className="text-3xl sm:text-4xl font-semibold mb-6 tracking-tight text-apple-50 max-w-3xl">
            Approval-gated moves, with a fully local option for privileged work
          </h2>

          <div className="max-w-3xl space-y-5 text-apple-200 leading-relaxed">
            <p>
              Sortio reads files on your Mac. When the AI classifier extracts party names, dates,
              and document types, that inference happens against a model you choose: a local LLM
              like Ollama (so no document content ever crosses the network), your own OpenAI or
              Anthropic API key (BYOK, requests go from your Mac directly to your provider), or
              the Sortio classifier as the default.
            </p>
            <p>
              The only outbound network call after classification is the upload to Clio, when the
              integration is enabled and you approve the batch. That goes Mac to Clio, not through
              our servers. Privilege and work product stay inside the same legal channel you
              already use.
            </p>
            <p>
              For retention, the Rule Builder lets you encode your state bar's rules once and apply
              them across every closed matter. Files get tagged with the closing date and the
              retention class, archives are flagged when their windows expire, and the audit log is
              ready when a bar inquiry asks who touched what.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-10">
            <div className="bg-apple-800/40 rounded-2xl border border-apple-700/50 p-6">
              <div className="flex items-center mb-3">
                <Lock className="text-sortio-primary mr-2" size={18} />
                <h3 className="text-base font-medium text-apple-50">Fully local inference (optional)</h3>
              </div>
              <p className="text-sm text-apple-200 leading-relaxed">
                Choose the Sortio default, BYOK against your own provider, or a fully local LLM
                like Ollama. Local-LLM mode means matter names and filenames never cross the
                network at all.
              </p>
            </div>

            <div className="bg-apple-800/40 rounded-2xl border border-apple-700/50 p-6">
              <div className="flex items-center mb-3">
                <FileCheck2 className="text-sortio-primary mr-2" size={18} />
                <h3 className="text-base font-medium text-apple-50">Clio-native uploads</h3>
              </div>
              <p className="text-sm text-apple-200 leading-relaxed">
                When connected, files upload directly to Clio's document API. They appear in the
                matter's document tree as if a paralegal uploaded them through the Clio web app.
              </p>
            </div>

            <div className="bg-apple-800/40 rounded-2xl border border-apple-700/50 p-6">
              <div className="flex items-center mb-3">
                <ScrollText className="text-sortio-primary mr-2" size={18} />
                <h3 className="text-base font-medium text-apple-50">Audit trail per file</h3>
              </div>
              <p className="text-sm text-apple-200 leading-relaxed">
                Every move and upload is logged locally with timestamp, original path, destination
                matter, classification confidence score, and the user that approved it. The same
                metadata is attached as a note on each Clio upload.
              </p>
            </div>

            <div className="bg-apple-800/40 rounded-2xl border border-apple-700/50 p-6">
              <div className="flex items-center mb-3">
                <ShieldCheck className="text-sortio-primary mr-2" size={18} />
                <h3 className="text-base font-medium text-apple-50">Retention rules</h3>
              </div>
              <p className="text-sm text-apple-200 leading-relaxed">
                Encode your state bar's retention rule once. Closed matters auto-archive with a
                closing date and retention class tag, and the same rule flags archives for
                deletion review when the window expires.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default RetentionAndPrivacy;
